/**
 * Phase 8 · Cluster C-43 — Shared sourcing connector contract.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.7
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.7
 *
 * Every sourcing connector (internal / linkedin / naukri today; github /
 * gitlab / discord in C-44; fiverr / upwork / peerlist in C-46) implements
 * `SourcingConnector` and is invoked from a wrapper tool registered with
 * the agent `toolRegistry`. The agent runner never talks to a connector
 * directly — always through the registry, so the audit row in
 * `ggj_b2b_agent_tool_invocations` captures the call.
 *
 * `SourcingResult` is the candidate-side shape only; the C-46 extension
 * introduces a `SourcingJobResult` twin for the jobs-capable connectors
 * (see §33.9). We intentionally keep the C-43 API candidate-first so
 * existing connectors do not need to declare `capabilities` yet.
 */

export type SourcingSource =
  | 'linkedin'
  | 'naukri'
  | 'github'
  | 'gitlab'
  | 'discord'
  | 'internal';

/**
 * Caller-supplied query. `limit` is capped at 100 by the connector
 * implementation; callers should pass ≤ 20 for interactive flows to keep
 * the LLM prompt budget small.
 */
export interface SourcingQuery {
  keywords: string[];
  location?: string;
  experienceYearsMin?: number;
  experienceYearsMax?: number;
  skills?: string[];
  /** Default 20, hard-capped at 100. */
  limit?: number;
}

/**
 * A single candidate surfaced by a connector. `signals` is free-form per
 * connector (e.g. { repoCount, topLanguage } for GitHub) — renderers
 * display known fields and drop the rest.
 */
export interface SourcingResult {
  externalId: string;
  name: string;
  headline?: string;
  locationText?: string;
  profileUrl: string;
  avatarUrl?: string;
  signals: Record<string, unknown>;
  source: SourcingSource;
}

export interface ConnectorHealth {
  ok: boolean;
  reason?: string;
}

export interface SourcingConnector {
  kind: SourcingSource;
  search(query: SourcingQuery, orgId: string): Promise<SourcingResult[]>;
  healthcheck(orgId: string): Promise<ConnectorHealth>;
}

/** Default limit + ceiling for every connector implementation. */
export const DEFAULT_SOURCING_LIMIT = 20;
export const MAX_SOURCING_LIMIT = 100;

/**
 * Thrown by a connector when the org has not configured it. The runner
 * catches this and surfaces a friendly "X is not configured for this
 * org" chip without logging a red error. Keeping it as a distinct class
 * means future retry/backoff logic can opt-out for this case.
 */
export class ConnectorNotConfiguredError extends Error {
  readonly kind: SourcingSource;
  constructor(kind: SourcingSource, extra?: string) {
    super(
      `${labelFor(kind)} is not configured for this org${extra ? ` — ${extra}` : ''}. Go to Settings → Integrations → Sourcing connectors.`,
    );
    this.name = 'ConnectorNotConfiguredError';
    this.kind = kind;
  }
}

/**
 * Thrown when the connector is configured but the remote provider cannot
 * be reached from the current runtime (usually a CORS block in the
 * browser path; the production path routes through an Edge Function
 * proxy). The runner translates this to an `ok:false` tool result so the
 * user sees a clear "couldn't reach LinkedIn right now" rather than a
 * silent empty result set.
 */
export class ConnectorTransportError extends Error {
  readonly kind: SourcingSource;
  constructor(kind: SourcingSource, reason: string) {
    super(`${labelFor(kind)} transport error: ${reason}`);
    this.name = 'ConnectorTransportError';
    this.kind = kind;
  }
}

/** Display label — kept here so every connector + the UI share one source. */
export function labelFor(kind: SourcingSource): string {
  switch (kind) {
    case 'linkedin':
      return 'LinkedIn';
    case 'naukri':
      return 'Naukri';
    case 'github':
      return 'GitHub';
    case 'gitlab':
      return 'GitLab';
    case 'discord':
      return 'Discord';
    case 'internal':
      return 'Internal pool';
  }
}

/** Clamp a user-provided limit to the connector protocol bounds. */
export function clampLimit(n: number | undefined): number {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return DEFAULT_SOURCING_LIMIT;
  return Math.min(Math.floor(n), MAX_SOURCING_LIMIT);
}
